import { useState } from 'react';
import { api } from '../api';
import { RADII, type Radius } from '../state/url-state';
import { HONEYPOT_FIELD } from '../honeypot';

/** "Get this weekend's sales" box: anonymous email+ZIP+radius entry point for the weekly
 * newsletter. POSTs /subscribe, which creates the account (if needed) and activates the
 * subscription immediately -- no confirmation click required (single opt-in, 2026-10-15). A
 * welcome email follows with a one-click unsubscribe link, but it's a notice, not a gate; this
 * box never learns whether the email already had an account. */
export function SubscribeBox() {
  const [email, setEmail] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [radius, setRadius] = useState<Radius>(25);
  const [fax, setFax] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState('');

  if (sent) {
    return (
      <div className="subscribe-box">
        <p className="hint">You&rsquo;re subscribed! We sent {email} a welcome note with a one-click unsubscribe link.</p>
      </div>
    );
  }

  return (
    <form
      className="subscribe-box"
      onSubmit={async (e) => {
        e.preventDefault();
        if (!/^\d{5}$/.test(postalCode)) {
          setErr('Enter a 5-digit ZIP code');
          return;
        }
        setBusy(true);
        setErr('');
        try {
          await api.postJson('/subscribe', { email, postal_code: postalCode, radius_mi: radius, [HONEYPOT_FIELD]: fax });
          setSent(true);
        } catch {
          setErr('Something went wrong. Try again.');
        } finally {
          setBusy(false);
        }
      }}
    >
      <h3>Get this weekend&rsquo;s sales</h3>
      <p className="hint">A free weekly email of sales near you.</p>
      <div className="row4">
        <label className="sr-only" htmlFor="subscribe-email">
          Email
        </label>
        <input
          id="subscribe-email"
          type="email"
          required
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <label className="sr-only" htmlFor="subscribe-zip">
          ZIP code
        </label>
        <input
          id="subscribe-zip"
          type="text"
          inputMode="numeric"
          pattern="\d{5}"
          required
          placeholder="ZIP"
          maxLength={5}
          value={postalCode}
          onChange={(e) => setPostalCode(e.target.value.replace(/\D/g, '').slice(0, 5))}
        />
        <label className="sr-only" htmlFor="subscribe-radius">
          Radius
        </label>
        <select id="subscribe-radius" value={radius} onChange={(e) => setRadius(Number(e.target.value) as Radius)}>
          {RADII.map((r) => (
            <option key={r} value={r}>
              {r} mi
            </option>
          ))}
        </select>
        <button className="primary" type="submit" disabled={busy || !email || !postalCode}>
          Sign me up
        </button>
      </div>
      {/* honeypot — hidden from people, irresistible to bots */}
      <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, overflow: 'hidden' }}>
        <label htmlFor="subscribe-fax">Fax number</label>
        <input id="subscribe-fax" name={HONEYPOT_FIELD} tabIndex={-1} autoComplete="off" value={fax} onChange={(e) => setFax(e.target.value)} />
      </div>
      {err && (
        <p className="error" role="alert">
          {err}
        </p>
      )}
    </form>
  );
}
